// src/pages/PatientCurrentMarkersPage.jsx
// Route: /patient/markers
//
// Patient-facing mount of EffectivePanelTab, in patientView mode — Current
// + Recommended Retests only. The navigator and doctor portals mount the
// same component without patientView and keep the full fresh/stale/
// never-tested breakdown; patients found "Stale (4)" and "Never Tested (9)"
// sitting next to "Recommended Retests (6)" read as three separate
// problems rather than one, when Recommended already is the actionable
// subset of exactly those two buckets. See EffectivePanelTab.jsx's own
// header comment for the full reasoning.
//
// Now a full "Markers" tab (MyHealthTabs) alongside Health Story and Care
// Plan, not a link-out from a summary card on Health Story — it earns a
// tab because it's a full page in its own right, the same reasoning that
// already put Health Story and Care Plan there.
//
// This is the piece that was missing for patients: the backend has always
// computed a full, per-draw effective panel regardless of how narrow any
// single report is, but until this page existed a patient had no way to
// see it — only navigators and doctors could.
//
// Also fetches triage-history (same endpoint PatientHealthTimeline uses)
// and builds a per-marker trend map from it, so clicking a marker card
// shows that marker's own sparkline — this is where the old Health Story
// "Marker trends" section's data went, moved here since a marker's trend
// belongs next to that marker's current value, not in a separate top-6
// pick on another page.

import React, { useEffect, useState, useCallback } from 'react';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { healthRecordAPI } from '../services/api';
import { buildMarkerHistoryMap } from '../utils/markerHistory';
import EffectivePanelTab from '../components/healthrecords/EffectivePanelTab';
import PageHead from '../components/PageHead';
import MyHealthTabs from '../components/MyHealthTabs';

export default function PatientCurrentMarkersPage() {
  const { patient } = usePatient();
  const [markerHistory, setMarkerHistory] = useState({});
  // Reported up by EffectivePanelTab once the panel loads — the page head's
  // stat strip needs the same numbers the panel below is built from.
  const [counts, setCounts] = useState(null);

  const loadMarkerHistory = useCallback(async () => {
    if (!patient?.id) return;
    try {
      const res = await healthRecordAPI.getTriageHistory(patient.id);
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setMarkerHistory(buildMarkerHistoryMap(data));
    } catch {
      // A marker card without a trend just renders without one (see
      // PatientMarkerCard) — losing the click-to-see-trend feature isn't
      // worth failing the whole page over.
    }
  }, [patient?.id]);

  useEffect(() => { loadMarkerHistory(); }, [loadMarkerHistory]);

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Health', to: '/patient/timeline' }, { label: 'Markers' }]}>
      <div className="page-shell page-shell--health">
        <MyHealthTabs />

        <PageHead
          eyebrow="My health timeline"
          title="Your current markers"
          subtitle="See what needs attention, then browse your latest results by health area."
          actions={counts && (
            <div className="mk-stats">
              <div className="mk-stat">
                <div className="mk-stat__value">{counts.current}</div>
                <div className="mk-stat__label">Current markers</div>
              </div>
              <div className="mk-stat">
                <div className="mk-stat__value mk-stat__value--attn">{counts.attention}</div>
                <div className="mk-stat__label">Need attention</div>
              </div>
              <div className="mk-stat">
                <div className="mk-stat__value">{counts.areas}</div>
                <div className="mk-stat__label">Health areas</div>
              </div>
            </div>
          )}
        />

        <EffectivePanelTab patient={patient} patientView markerHistory={markerHistory} onLoaded={setCounts} />
      </div>
    </PatientAppShell>
  );
}
